import React from 'react';
import {
  Button,
  Gutter,
  Modal,
  ModalHeader,
  ModalSection,
  ModalFooter,
  Checkbox,
  Form,
  Accolade,
  Icon,
  SectionHeader,
  Well,
  ParagraphHeader,
  Paragraph,
} from '@axiom/ui';
import type { Candidate, CandidateWorkFeedItem } from '@axiom/validation';
import { CurrencyUtil } from '@axiom/utils';

import { WorkFeedSelfServiceModalSchema } from './work-feed-self-service-modal-schema';

export type WorkFeedSelfServiceModalProps = {
  candidate: Candidate;
  name?: string;
  onAfterSubmit: (() => void) | (() => Promise<void>);
  onClose: () => void;
  workFeedItem: CandidateWorkFeedItem;
};

export const WorkFeedSelfServiceModal = ({
  candidate,
  name = 'WORK_FEED_SELF_SERVICE_MODAL',
  onAfterSubmit,
  onClose,
  workFeedItem,
}: WorkFeedSelfServiceModalProps) => {
  /**
   * The "??" are here to appease the TS gods.
   * At this point in the code these values should never be NULL/Undefined
   * */
  const currencyType = candidate.currencyType ?? 'USD';
  const formatRate = (value: number | string | null | undefined): string =>
    CurrencyUtil.getFormattedCurrency({ value, currencyCode: currencyType }) ??
    `${CurrencyUtil.convertToSymbol(currencyType)} 0.00`;
  const compHourlyPremiumFormatted = formatRate(candidate.compHourlyPremium);
  const hourlyCompensationFormatted = formatRate(candidate.hourlyCompensation);
  const compHourly40hrFormatted = formatRate(candidate.compHourly40hr);

  return (
    <Form
      name={`${name}_FORM`}
      schema={WorkFeedSelfServiceModalSchema}
      initialValues={{
        agree: false,
      }}
      onSubmit={async () => {
        await onAfterSubmit();
      }}
    >
      {({ fireSubmit }) => {
        return (
          <Modal name={name}>
            <ModalHeader
              name="PENDO_WORK_FEED_SELF_SERVICE_MODAL_HEADER"
              onClose={() => {
                onClose();
              }}
            >
              Submit profile to{' '}
              {workFeedItem.opportunity.accountName ?? 'Client'}
            </ModalHeader>
            <ModalSection>
              <Gutter bottom="16px">
                <Accolade
                  color="purple"
                  label="Self-service"
                  name="SELF_SERVICE"
                >
                  <Icon name="star-filled" />
                </Accolade>
              </Gutter>
              <SectionHeader>
                Submit your profile directly to the client.
              </SectionHeader>
              <Gutter bottom="16px" />
              <Well background="gray">
                <ParagraphHeader>
                  Before submitting your profile for this Self-Service
                  opportunity:
                </ParagraphHeader>
                <Gutter bottom="8px" />
                <ul>
                  <li>
                    <Paragraph>
                      Ensure your qualifications match up well with the
                      opportunity and that your profile is up to date.
                    </Paragraph>
                    <Gutter bottom="8px" />
                  </li>
                  <li>
                    <Paragraph>
                      Confirm that your interview availability calendar has at
                      least 8 hours of availability over the next 2 weeks.
                    </Paragraph>
                    <Gutter bottom="8px" />
                  </li>
                  <li>
                    <Paragraph>
                      Be ready to respond to client interview requests and
                      prepare for the call.
                    </Paragraph>
                    <Gutter bottom="8px" />
                  </li>
                  <li>
                    <Paragraph>Confirm your pay rate.</Paragraph>
                  </li>
                </ul>
              </Well>
              <Gutter bottom="16px" />
              <Checkbox
                label="Confirm pay rate"
                name="agree"
                displayValue={`${
                  workFeedItem.position.billingHoursPerWeek
                    ? `This opportunity is currently expected to be ${workFeedItem.position.billingHoursPerWeek} hours per week.`
                    : 'The weekly commitment for this opportunity is currently unknown.'
                } You will be paid ${compHourly40hrFormatted}/hr for opportunities that are 40 hours per week; ${hourlyCompensationFormatted}/hr for opportunities less than 40 hours per week; and ${compHourlyPremiumFormatted}/hr for opportunities below 20 hours per week.`}
              />
              <Gutter bottom="24px" only="smallScreen" />
            </ModalSection>
            <ModalFooter>
              <Button
                variation="outline"
                name="PENDO_WORK_FEED_SELF_SERVICE_MODAL_CANCEL"
                onClick={() => {
                  onClose();
                }}
              >
                Cancel
              </Button>

              <Button
                name="PENDO_WORK_FEED_SELF_SERVICE_MODAL_SUBMIT"
                onClick={fireSubmit}
              >
                Next
              </Button>
            </ModalFooter>
          </Modal>
        );
      }}
    </Form>
  );
};
